import React, { FC, useState, useEffect } from "react";
import { Typography, Box, Button } from "@orion/ui";
import { LeftLine } from "@orion/icons";
import LoginForm from "./LoginForm";
import { LoginContext } from "../context";
import { useTranslation } from "react-i18next";
import { useLoginForm } from "../hooks";
import AuthFailureNotification from "./AuthFailureNotification";
import NotificationScreen, { LocationType } from "./NotificationScreen";
import QRCodeScreen from "./QRCodeScreen";
import { LoginScreen } from "../types";
import CodeVerification from "./CodeVerification";
import { RootStateOrAny, useSelector } from "react-redux";
import { useErrorContext } from "../../_shared/context/ErrorContext";
import { useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import { getEnvVar } from "@delorean/utils/env-utils";
import * as loginRoutes from '../../_shared/routing';
import { redirectToFallback } from "@delorean/utils/securityUtils";

type Props = {
  passwordExpired?: boolean;
};

const Login: FC<Props> = ({ passwordExpired }) => {
  const { t } = useTranslation();
  const { confirmation, setConfirmation, screen, setScreen } = useLoginForm();
  const { hideError } = useErrorContext();
  const location = useLocation();
  const [cookies, , removeCookie] = useCookies(['auth_flow_token']);
    const isAuthenticated = useSelector(
    (state: RootStateOrAny) => state.login.isAuthenticated
  );


  // Flow validation OIDC - runs only once on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    //if nexi return redirect with error params
    const hasError = params.has('error');
    if (hasError) {
      redirectToFallback("Unauthorized access");
      return;
    }

    const isComingFromResetPasswordState = location.state && (location.state as any).fromResetPassword;
    console.log("isComingFromResetPasswordState:", isComingFromResetPasswordState);

    if (isAuthenticated && isComingFromResetPasswordState) {
      console.log("coming from reset password - proceed with login flow");
      return;
    }

    if (!cookies.auth_flow_token) {
      console.warn("Direct access to /login without going through /authorize");
      redirectToFallback("You must access through the main application.");
      return;
    }

    // Extra guard: if the cookie exists but there is no referrer, the user likely
    // typed the URL directly in the browser with a manually set cookie, so redirect to fallback to avoid possible redirect work-around
    if (cookies.auth_flow_token && !document.referrer) {
      console.warn("Direct access to /login without going through /authorize");
      redirectToFallback("You must access through the main application.");
      return;
    }

    removeCookie('auth_flow_token', { path: '/' });
  }, []);  // Error handling - runs only once on mount
  useEffect(() => {
    hideError();
  }, [screen, location.pathname]);

  // Gestisci il refresh della pagina quando viene richiesto via state resetLogin
  useEffect(() => {
    if (location.state && (location.state as any).resetLogin === true) {
      window.location.reload();
    }
  }, []);

  const goBack = () => {
    if (screen === LoginScreen.APP_QRCODE) {
      setScreen(LoginScreen.APP_NOTIFICATION);
      return;
    }
    setConfirmation(false);
    setScreen(LoginScreen.DEFAULT);
  };

  return (
    <Box flex column justifyCenter alignItems="center">
      {(screen === LoginScreen.FAILURE) && (
        <Box justifyStart maxWidth="3xl" width="full">
          <Button
            role="button"
            variant="ghostPrimary"
            flex
            justifyBetween
            gap7
            px0
            py2
            alignItems="flex-start"
            style={{ cursor: "pointer", maxWidth: "5.688rem" }}
            onPress={() => goBack()}
          >
            <LeftLine w11 />
            <Typography fontSize="md" lineHeight="md" fontWeight="medium">
              {t("login.back")}
            </Typography>
          </Button>
        </Box>
      )}
      <Box flex justifyCenter>
        {confirmation ? (
          <>
            {screen === LoginScreen.PHYS_TOKEN && <CodeVerification />}
            {screen === LoginScreen.APP_NOTIFICATION && <NotificationScreen location={LocationType.LOGIN} />}
            {screen === LoginScreen.APP_QRCODE && <QRCodeScreen />}
          </>
        ) : screen === LoginScreen.SOFT_BLOCKED_USER ? (
          <AuthFailureNotification
            btnPrimaryTitle={t("login.backToLandingPage")}
            btnPrimarySubmit={() => {setScreen(LoginScreen.DEFAULT)}}
            title={t("login.blockedUser.temporarilyBlockedUser")}
            subtitleOne={t("login.blockedUser.exceededAttempts")}
            support
          />
        ) : screen === LoginScreen.BLOCKED_USER ? (
          <AuthFailureNotification
            btnPrimaryTitle={t("login.backToLandingPage")}
            btnPrimarySubmit={() => {setScreen(LoginScreen.DEFAULT)}}
            title={t("login.blockedUser.blockedUser")}
            subtitleOne={t("login.blockedUser.securityReasons")}
            subtitleTwo={t("login.blockedUser.unlockUser")}
            support
          />
        ) : screen === LoginScreen.FAILURE ? (
          <AuthFailureNotification
            btnPrimaryTitle={t("login.backToLandingPage")}
            btnPrimarySubmit={() => {setScreen(LoginScreen.DEFAULT)}}
            title={t("login.authFailure.failure")}
            subtitleOne={t("login.authFailure.verifyIdentity")}
          />
        ) : (
          <LoginForm />
        )}
      </Box>
    </Box>
  );
};

export default ({ passwordExpired = false }: Props) => {
  const [contractCode, setContractCode] = useState<string>("");
  const [userCode, setUserCode] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  return (
    <LoginContext.Provider
      value={{
        password,
        setPassword,
        contractCode,
        setContractCode,
        userCode,
        setUserCode,
      }}
    >
      <Login passwordExpired={passwordExpired} />
    </LoginContext.Provider>
  );
};
